import React, { useEffect, useState } from 'react';
import { FieldError, UseControllerProps, useWatch } from 'react-hook-form';
import { useQuery } from 'react-query';
import { useDebounce } from 'use-debounce';
import { getProfiles } from '../../api/profile';
import { useAuth } from '../../contexts/Auth';
import { Profile } from '../../interfaces/profile';
import { getQuerySearchFilters } from '../../libs/utils/profile';
import { SearchableSelect } from '../Form/SearchableSelect';

type ProfileSearchSelectProps = {
  id?: string;
  label: string;
  disabled?: boolean;
  error?: FieldError;
  onProfileSelect?: (profile: Profile) => void;
} & UseControllerProps;

const ProfileSearchSelect: React.FC<ProfileSearchSelectProps> = ({
  control,
  name,
  label,
  disabled = false,
  error,
  defaultValue,
  onProfileSelect,
}) => {
  const [{ token }] = useAuth();
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch] = useDebounce(search, 500);
  const value = useWatch({ control, name }) as number;

  useEffect(() => {
    query.refetch();
  }, [debouncedSearch, value]);

  const query = useQuery(
    ['profiles-select', debouncedSearch],
    () => {
      const filters = getQuerySearchFilters(debouncedSearch);
      const q = {
        filters: {
          $or: [
            ...filters,
            {
              id: value,
            },
          ],
        },
        populate: ['badge'],
        pagination: {
          pageSize: 50,
        },
        forceListing: true,
      };
      return getProfiles({
        token,
        query: q,
      });
    },
    {
      cacheTime: 0,
      staleTime: 0,
    },
  );

  const options = React.useMemo(() => {
    if (!query?.data) return [];
    return query.data.data.map((profile: Profile) => {
      const badgeNumber =
        profile.attributes.badge?.data?.attributes?.badgeNumber || 'no';
      return {
        value: profile.id,
        label: `${profile.attributes.name} ${profile.attributes.surname} (${badgeNumber})`,
        profile,
      };
    });
  }, [query]);

  const handleChange = (value: (typeof options)[number]) => {
    onProfileSelect?.(value.profile);
  };

  return (
    <SearchableSelect
      label={label}
      name={name}
      /*       defaultValue={defaultValue} */
      disabled={disabled}
      control={control}
      error={error}
      onInput={setSearch}
      isLoading={query.isFetching}
      onChange={handleChange}
      options={options}
      registerOptions={{
        setValueAs: (value) => {
          return value.value;
        },
      }}
    />
  );
};

export default ProfileSearchSelect;
